import { AxiosError, AxiosRequestConfig } from 'axios';

export interface RetryConfig {
  maxRetries: number;
  retryDelay: number;
  retryDelayMultiplier: number;
  retryableStatusCodes: number[];
  shouldRetry?: (error: AxiosError) => boolean;
  onRetry?: (error: AxiosError, retryCount: number) => void;
}

export interface AxiosRequestConfigWithRetry extends AxiosRequestConfig {
  _retry?: boolean;
  _retryCount?: number;
  retryConfig?: Partial<RetryConfig>;
}

export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  retryDelay: 1000,
  retryDelayMultiplier: 2,
  retryableStatusCodes: [408, 429, 500, 502, 503, 504],
};

/**
 * Check if the error is a network error
 */
export function isNetworkError(error: AxiosError): boolean {
  return (
    !error.response &&
    Boolean(error.code) &&
    error.code !== 'ECONNABORTED' &&
    error.code !== 'ERR_CANCELED'
  );
}

/**
 * Check if the error is retryable based on the configuration
 */
export function isRetryableError(error: AxiosError, config: RetryConfig): boolean {
  // Check custom retry function first
  if (config.shouldRetry) {
    return config.shouldRetry(error);
  }

  // Network errors are always retryable
  if (isNetworkError(error)) {
    return true;
  }

  // Check if status code is retryable
  if (error.response && config.retryableStatusCodes.includes(error.response.status)) {
    return true;
  }

  return false;
}

/**
 * Calculate the retry delay with exponential backoff and jitter
 */
export function calculateRetryDelay(attempt: number, config: RetryConfig): number {
  // Base delay with exponential backoff
  const baseDelay = config.retryDelay * Math.pow(config.retryDelayMultiplier, attempt - 1);

  // Add jitter (±25% of base delay) to prevent thundering herd
  const jitter = baseDelay * 0.25 * (Math.random() * 2 - 1);

  // Cap at 30 seconds
  return Math.min(baseDelay + jitter, 30000);
}

/**
 * Sleep for the specified number of milliseconds
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Get the retry count from the request config
 */
export function getRetryCount(config: AxiosRequestConfigWithRetry): number {
  return config._retryCount || 0;
}

/**
 * Increment the retry count in the request config
 */
export function incrementRetryCount(config: AxiosRequestConfigWithRetry): number {
  const currentCount = getRetryCount(config);
  config._retryCount = currentCount + 1;
  return config._retryCount;
}

/**
 * Check if we should retry based on the current count and max retries
 */
export function canRetry(config: AxiosRequestConfigWithRetry, retryConfig: RetryConfig): boolean {
  const retryCount = getRetryCount(config);
  return retryCount < retryConfig.maxRetries;
}

/**
 * Merge retry configurations with defaults
 */
export function mergeRetryConfig(custom?: Partial<RetryConfig>): RetryConfig {
  return {
    ...DEFAULT_RETRY_CONFIG,
    ...custom,
  };
}
